import FileUploadIcon from "@mui/icons-material/FileUploadRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { useState } from "react";
import { formatFileSize } from "../../utilities/fileHelper";
import { ensureSvgFileHasSize } from "../../utilities/svgFileHelper";
import HiddenFileInput from "../inputs/HiddenFileInput";

interface Props {
  imageSrc: string | undefined;
  originalImageSrc?: string | undefined;
  allowedImageTypes: string[];
  maxFileSizeBytes: number;
  width: number | string;
  height: number | string;
  svgFallbackSize?: { width: number; height: number } | undefined;
  uploadButtonName: string;
  showUploadButtonIcon?: boolean | undefined;
  cancelButtonName?: string | undefined;
  buttonsPosition: "bottom" | "right";
  backgroundSize?: string | undefined;
  backgroundColor?: string | undefined;
  disabled?: boolean;
  border?: "solid" | "dashed" | undefined;
  Placeholder?: JSX.Element | JSX.Element[] | null;
  showPlaceholderOverImage?: boolean | undefined;
  onImageSelected: (file: File | undefined, url: string) => void;
}

const ImageUploaderWithPreview = ({
  imageSrc,
  originalImageSrc,
  allowedImageTypes,
  maxFileSizeBytes,
  width,
  height,
  svgFallbackSize,
  uploadButtonName,
  showUploadButtonIcon,
  cancelButtonName,
  buttonsPosition,
  backgroundSize,
  backgroundColor,
  disabled,
  border,
  Placeholder,
  showPlaceholderOverImage,
  onImageSelected,
}: Props) => {
  const [warningMsg, setWarningMsg] = useState("");

  const handleCancel = () => {
    onImageSelected(undefined, originalImageSrc || "");
    setWarningMsg("");
  };

  const handleImageFilesAdd = async (files: File[]) => {
    setWarningMsg("");

    let file = files[0];
    if (file === undefined) {
      return;
    }

    if (file.size > maxFileSizeBytes) {
      setWarningMsg(
        `Uploaded image size ${formatFileSize(file.size)} is too large. Max allowed size is ${formatFileSize(maxFileSizeBytes)}.`
      );
      return;
    }

    if (!allowedImageTypes.includes(file.type)) {
      setWarningMsg(`Uploaded file type '${file.type}' is not accepted.`);
      return;
    }

    file = await ensureSvgFileHasSize(file, svgFallbackSize?.height ?? height, svgFallbackSize?.width ?? width);
    onImageSelected(file, URL.createObjectURL(file));
  };

  return (
    <Stack>
      <Stack
        spacing={2}
        direction={buttonsPosition === "right" ? "row" : "column"}
        sx={{ alignItems: buttonsPosition === "right" ? "center" : "flex-start" }}
      >
        <Box
          sx={[
            {
              position: "relative",
              height: height,
              width: width,
              border: border ? `1px ${border} #E2E8F0` : "none",
              borderRadius: "4px",
            },
            (theme) => ({
              backgroundImage: `url(${imageSrc})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: backgroundSize || "contain",
              backgroundColor: backgroundColor || (imageSrc ? "transparent" : theme.palette.subtleSurfaceBg),
              backgroundPosition: "center center",
            }),
          ]}
        >
          {(!imageSrc || !!showPlaceholderOverImage) && Placeholder}
        </Box>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Button
            startIcon={showUploadButtonIcon ? <FileUploadIcon /> : undefined}
            variant="outlined"
            component="label"
            color="secondary"
            disabled={disabled}
          >
            {uploadButtonName}
            <HiddenFileInput acceptedFileTypes={allowedImageTypes} onFilesAdd={handleImageFilesAdd} />
          </Button>
          {originalImageSrc && (
            <Button
              variant="text"
              color="secondary"
              disabled={disabled || originalImageSrc === imageSrc}
              onClick={handleCancel}
            >
              {cancelButtonName || "Cancel"}
            </Button>
          )}
        </Stack>
      </Stack>
      {warningMsg && (
        <Typography color="error" sx={{ mt: 1 }}>
          {warningMsg}
        </Typography>
      )}
    </Stack>
  );
};

export default ImageUploaderWithPreview;
